import React from 'react';

interface RecentTestPerformance {
    testId: number;
    testName: string;
    courseName: string;
    score: number;
    isPassed: boolean;
    attemptDate: string;
    scoreChange: number;
}

interface DailyStudy {
    date: string;
    minutesSpent: number;
    questionsAnswered: number;
    testsCompleted: number;
    averageScore: number;
}

interface StudyTrend {
    dailyActivity: DailyStudy[];
    averageDailyMinutes: number;
    totalDaysActive: number;
    weeklyTrendPercentage: number;
}

interface PerformanceChartProps {
    recentTests: RecentTestPerformance[];
    studyTrend: StudyTrend;
}

export default function PerformanceChart({ recentTests, studyTrend }: PerformanceChartProps) {
    // Calculate average and max scores for chart scaling
    const avgScore = recentTests.length > 0 
        ? recentTests.reduce((sum, test) => sum + test.score, 0) / recentTests.length 
        : 0;

    const maxScore = Math.max(...recentTests.map(t => t.score), 100);

    return (
        <div className="space-y-6">
            {/* Performance Trend Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Performance Trend</h2>
                
                {recentTests.length > 0 ? (
                    <div className="space-y-4">
                        {/* Simple bar chart */}
                        <div className="flex items-end gap-2 h-64 pb-8">
                            {recentTests.slice().reverse().map((test, idx) => (
                                <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                                    <div 
                                        className={`w-full rounded-t-lg transition-all ${
                                            test.isPassed ? 'bg-green-500' : 'bg-red-500'
                                        }`}
                                        style={{ height: `${(test.score / maxScore) * 200}px` }}
                                        title={`${test.testName}: ${test.score.toFixed(1)}%`}
                                    />
                                    <p className="text-xs text-gray-600 text-center font-medium">
                                        {test.score.toFixed(0)}%
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Legend */}
                        <div className="flex justify-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-green-500 rounded"></div>
                                <span className="text-gray-600">Passed (≥70%)</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 bg-red-500 rounded"></div>
                                <span className="text-gray-600">Failed</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-gray-500 text-center py-8">No test data available</p>
                )}
            </div>

            {/* Study Pattern */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Study Pattern (Last 30 Days)</h2>
                
                {studyTrend.dailyActivity.length > 0 ? (
                    <div className="space-y-4">
                        {/* Activity heatmap-style grid */}
                        <div className="grid grid-cols-7 gap-2">
                            {studyTrend.dailyActivity.map((day, idx) => {
                                const intensity = Math.min(day.minutesSpent / 60, 1); // Normalize to 0-1
                                const intensity_pct = intensity * 100;
                                
                                return (
                                    <div
                                        key={idx}
                                        className="aspect-square rounded-lg border border-gray-300 flex items-center justify-center cursor-pointer hover:border-indigo-600 transition group relative"
                                        style={{
                                            backgroundColor: `rgba(99, 102, 241, ${intensity * 0.7})`
                                        }}
                                        title={`${new Date(day.date).toLocaleDateString()}: ${day.minutesSpent}min`}
                                    >
                                        <span className="text-xs font-semibold text-gray-800">
                                            {new Date(day.date).getDate()}
                                        </span>
                                        
                                        {/* Tooltip */}
                                        <div className="absolute hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap">
                                            {day.minutesSpent}min
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Statistics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                            <div className="p-4 bg-blue-50 rounded-lg">
                                <p className="text-xs text-gray-600 font-medium">Avg Daily Study</p>
                                <p className="text-2xl font-bold text-blue-600">{studyTrend.averageDailyMinutes}m</p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-lg">
                                <p className="text-xs text-gray-600 font-medium">Active Days</p>
                                <p className="text-2xl font-bold text-green-600">{studyTrend.totalDaysActive}</p>
                            </div>
                            <div className="p-4 bg-purple-50 rounded-lg">
                                <p className="text-xs text-gray-600 font-medium">Weekly Trend</p>
                                <p className={`text-2xl font-bold ${studyTrend.weeklyTrendPercentage >= 0 ? 'text-purple-600' : 'text-red-600'}`}>
                                    {studyTrend.weeklyTrendPercentage > 0 ? '+' : ''}{studyTrend.weeklyTrendPercentage.toFixed(0)}%
                                </p>
                            </div>
                            <div className="p-4 bg-orange-50 rounded-lg">
                                <p className="text-xs text-gray-600 font-medium">Total Study</p>
                                <p className="text-2xl font-bold text-orange-600">
                                    {(studyTrend.dailyActivity.reduce((sum, d) => sum + d.minutesSpent, 0) / 60).toFixed(1)}h
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-gray-500 text-center py-8">No study data available</p>
                )}
            </div>
        </div>
    );
}
